/**
 * Merchant Settings Page (Story 3.1)
 *
 * Provides merchant profile viewing and editing functionality.
 * - View/edit merchant name and description
 * - Display merchant logo (if set in database)
 * - Customer registration link display and copy (Story 3.6)
 * - Client-side validation with inline errors
 *
 * Features:
 * - React Hook Form with Zod validation
 * - Dirty state tracking for Save button
 * - Success/error toasts
 */

import { useEffect, useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { updateMerchantProfileBodySchema } from '@zlinkx/shared-v2';
import type { UpdateMerchantProfileBodyType } from '@zlinkx/shared-v2';
import { useMerchantProfile } from '@/hooks-v2/aggregator';
import { useCurrentMerchant } from '@/hooks-v2/context';
import { useMerchantProfileActions } from '@/hooks-v2/coordinator';
import { useTranslation } from '@/i18n';
import { i18nToast } from '@/utils/i18n-toast';
import { Save, AlertCircle, Copy, CheckCircle2 } from 'lucide-react';

// Validation schema for merchant profile form — derived from shared-v2 contract
// We pick only the fields editable in this form and add client-side min-length for UX
const merchantProfileSchema = updateMerchantProfileBodySchema
	.pick({ merchant_name: true, merchant_description: true })
	.extend({
		merchant_name: z
			.string()
			.min(2, 'merchant.settings.validation.nameMin')
			.max(200, 'merchant.settings.validation.nameMax'),
		merchant_description: z.string().max(500, 'merchant.settings.validation.descriptionMax'),
	});

type MerchantProfileFormData = z.infer<typeof merchantProfileSchema>;

const Settings = () => {
	const { t } = useTranslation();
	const customerAppUrl = (() => {
		const envValue = (import.meta.env as Record<string, unknown>).VITE_CUSTOMER_APP_URL;
		return typeof envValue === 'string' && envValue.length > 0 ? envValue : 'https://zlinkx.app';
	})();

	// State for copy button feedback
	const [linkCopied, setLinkCopied] = useState(false);

	const { selectedMerchant, loading: isLoadingMerchants } = useCurrentMerchant();

	// Fetch merchant profile data using feature hook
	const merchantProfile = useMerchantProfile({
		merchantId: selectedMerchant?.merchantId ?? 0,
		enabled: selectedMerchant !== null && selectedMerchant.merchantId > 0,
	});

	// Extract profile data for easier access
	const merchantData = merchantProfile.data;
	const isLoadingMerchant = merchantProfile.isLoading;
	const merchantError = merchantProfile.error;

	// Update actions using coordinator hook
	const profileActions = useMerchantProfileActions();

	// Track submission state to prevent double-submit
	const [isSubmitting, setIsSubmitting] = useState(false);

	// Form setup with RHF + Zod
	const {
		register,
		handleSubmit,
		formState: { errors, isDirty, dirtyFields },
		reset,
		watch,
	} = useForm<MerchantProfileFormData>({
		resolver: zodResolver(merchantProfileSchema),
		defaultValues: {
			merchant_name: '',
			merchant_description: '',
		},
	});

	// Watch form values for character count
	const descriptionValue = watch('merchant_description') || '';
	const requiredFieldMarker = String.fromCharCode(42);
	const registrationSlug = merchantData.slug;

	// Populate form when merchant data loads
	useEffect(() => {
		reset({
			merchant_name: merchantData.merchantName,
			merchant_description: merchantData.merchantDescription || '',
		});
	}, [merchantData.merchantName, merchantData.merchantDescription, reset]);

	// Warn on browser/tab close when dirty (in-app navigation blocking removed - requires data router)
	useEffect(() => {
		const handleBeforeUnload = (e: BeforeUnloadEvent) => {
			if (isDirty) {
				e.preventDefault();
				e.returnValue = '';
				return '';
			}
		};

		window.addEventListener('beforeunload', handleBeforeUnload);
		return () => window.removeEventListener('beforeunload', handleBeforeUnload);
	}, [isDirty]);

	// Handle copying registration link
	const handleCopyLink = useCallback(
		async (slug: string) => {
			const registrationLink = `${customerAppUrl}/join/${slug}`;
			try {
				await navigator.clipboard.writeText(registrationLink);
				setLinkCopied(true);
				i18nToast.success('merchant.settings.linkCopied');
				// Reset feedback after 2 seconds
				setTimeout(() => setLinkCopied(false), 2000);
			} catch (error) {
				console.error('Failed to copy link:', error);
				i18nToast.error('merchant.settings.linkCopyFailed');
			}
		},
		[customerAppUrl],
	);

	// Handle form submission
	const onSubmit = useCallback(
		async (data: MerchantProfileFormData) => {
			if (selectedMerchant === null || selectedMerchant.merchantId <= 0 || isSubmitting) return;

			setIsSubmitting(true);

			try {
				// Only send changed fields
				const changedFields: UpdateMerchantProfileBodyType = {};
				if (dirtyFields.merchant_name) {
					changedFields.merchant_name = data.merchant_name;
				}
				if (dirtyFields.merchant_description) {
					changedFields.merchant_description = data.merchant_description.trim();
				}

				// If no fields changed, do nothing
				if (Object.keys(changedFields).length === 0) {
					setIsSubmitting(false);
					return;
				}

				await profileActions.updateProfile(
					selectedMerchant.merchantId,
					changedFields,
				);

				// Reset form with new values to clear dirty state
				reset(data);
				i18nToast.success('toast.merchant.profileUpdated');
			} catch (error) {
				console.error('Failed to update merchant profile:', error);
				i18nToast.error('merchant.settings.updateFailed');
			} finally {
				setIsSubmitting(false);
			}
		},
		[selectedMerchant, isSubmitting, dirtyFields, profileActions, reset],
	);

	const onFormSubmit: React.FormEventHandler<HTMLFormElement> = event => {
		void handleSubmit(onSubmit)(event);
	};

	// Loading state
	if (isLoadingMerchants) {
		return (
			<div className='flex items-center justify-center min-h-screen'>
				<div className='text-center'>
					<div className='animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4'></div>
					<p className='text-gray-600'>{t('merchant.settings.loadingMerchant')}</p>
				</div>
			</div>
		);
	}

	// No merchant selected
	if (selectedMerchant === null) {
		return (
			<div className='flex items-center justify-center min-h-screen'>
				<div className='text-center'>
					<h2 className='text-2xl font-bold text-gray-900 mb-4'>
						{t('merchant.settings.noMerchantTitle')}
					</h2>
					<p className='text-gray-600'>{t('merchant.settings.noMerchantMessage')}</p>
				</div>
			</div>
		);
	}

	// Loading merchant data
	if (isLoadingMerchant) {
		return (
			<div className='flex items-center justify-center min-h-screen'>
				<div className='text-center'>
					<div className='animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4'></div>
					<p className='text-gray-600'>{t('merchant.settings.loadingProfile')}</p>
				</div>
			</div>
		);
	}

	// Error state
	if (merchantError) {
		return (
			<div className='flex items-center justify-center min-h-screen'>
				<div className='text-center'>
					<AlertCircle className='h-12 w-12 text-red-500 mx-auto mb-4' />
					<h2 className='text-2xl font-bold text-gray-900 mb-4'>
						{t('merchant.settings.errorTitle')}
					</h2>
					<p className='text-gray-600'>{t('merchant.settings.errorMessage')}</p>
				</div>
			</div>
		);
	}

	return (
		<div className='space-y-4 sm:space-y-6 p-4 sm:p-6'>
			{/* Header */}
			<div className='bg-white rounded-xl shadow-sm border p-4 sm:p-6'>
				<div className='flex flex-col space-y-2'>
					<h1 className='text-xl sm:text-2xl font-bold text-gray-900'>
						{t('merchant.settings.title')}
					</h1>
					<p className='text-gray-600 text-sm sm:text-base'>
						{t('merchant.settings.subtitle', { merchantName: selectedMerchant.merchantName })}
					</p>
				</div>
			</div>

			{/* Profile Form */}
			<form onSubmit={onFormSubmit} className='space-y-4 sm:space-y-6'>
				{/* Logo Display Section (logo set via database) */}
				{merchantData.logoUrl && (
					<div className='bg-white rounded-xl shadow-sm border p-4 sm:p-6'>
						<h2 className='text-base sm:text-lg font-semibold text-gray-900 mb-4'>
							{t('merchant.settings.logoSection')}
						</h2>
						<div className='h-24 w-24 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center overflow-hidden'>
							<img
								src={merchantData.logoUrl}
								alt={t('merchant.settings.logoAlt')}
								className='h-full w-full object-cover'
							/>
						</div>
					</div>
				)}

				{/* Profile Details */}
				<div className='bg-white rounded-xl shadow-sm border p-4 sm:p-6'>
					<h2 className='text-base sm:text-lg font-semibold text-gray-900 mb-4'>
						{t('merchant.settings.profileSection')}
					</h2>

					<div className='space-y-4'>
						{/* Merchant Name */}
						<div>
							<label htmlFor='merchant_name' className='block text-sm font-medium text-gray-700 mb-1'>
								{t('merchant.settings.nameLabel')} <span className='text-red-500'>{requiredFieldMarker}</span>
							</label>
							<input
								id='merchant_name'
								type='text'
								{...register('merchant_name')}
								className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base ${
									errors.merchant_name ? 'border-red-500' : 'border-gray-300'
								}`}
								placeholder={t('merchant.settings.namePlaceholder')}
								aria-invalid={!!errors.merchant_name}
								aria-describedby={errors.merchant_name ? 'name-error' : undefined}
							/>
							{errors.merchant_name && (
								<p id='name-error' className='mt-1 text-sm text-red-600 flex items-center'>
									<AlertCircle className='h-4 w-4 mr-1' />
									{t(errors.merchant_name.message || 'merchant.settings.validation.nameRequired')}
								</p>
							)}
							<p className='mt-1 text-xs text-gray-500'>{t('merchant.settings.nameHint')}</p>
						</div>

						{/* Merchant Description */}
						<div>
							<label
								htmlFor='merchant_description'
								className='block text-sm font-medium text-gray-700 mb-1'
							>
								{t('merchant.settings.descriptionLabel')}
							</label>
							<textarea
								id='merchant_description'
								{...register('merchant_description')}
								rows={4}
								className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base resize-none ${
									errors.merchant_description ? 'border-red-500' : 'border-gray-300'
								}`}
								placeholder={t('merchant.settings.descriptionPlaceholder')}
								aria-invalid={!!errors.merchant_description}
								aria-describedby={errors.merchant_description ? 'description-error' : undefined}
							/>
							<div className='mt-1 flex justify-between'>
								{errors.merchant_description ? (
									<p id='description-error' className='text-sm text-red-600 flex items-center'>
										<AlertCircle className='h-4 w-4 mr-1' />
										{t(errors.merchant_description.message || '')}
									</p>
								) : (
									<p className='text-xs text-gray-500'>{t('merchant.settings.descriptionHint')}</p>
								)}
								<p
									className={`text-xs ${
										descriptionValue.length > 500 ? 'text-red-500' : 'text-gray-500'
									}`}
								>
									{descriptionValue.length}/500
								</p>
							</div>
						</div>
					</div>
				</div>

				{/* Registration Link Section (Story 3.6) */}
				<div className='bg-white rounded-xl shadow-sm border p-4 sm:p-6'>
					<h2 className='text-base sm:text-lg font-semibold text-gray-900 mb-4'>
						{t('merchant.settings.registrationLinkSection')}
					</h2>

					{registrationSlug ? (
						<>
							<p className='text-sm text-gray-600 mb-3'>
								{t('merchant.settings.registrationLinkHint')}
							</p>
							<div className='flex flex-col sm:flex-row items-stretch sm:items-center gap-3'>
								<input
									readOnly
									value={`${customerAppUrl}/join/${registrationSlug}`}
									className='flex-1 px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm font-mono text-gray-700 cursor-default'
									onClick={(e) => e.currentTarget.select()}
								/>
								<button
									type='button'
									onClick={() => {
										void handleCopyLink(registrationSlug);
									}}
									className='inline-flex items-center justify-center px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors'
								>
									{linkCopied ? (
										<>
											<CheckCircle2 className='h-4 w-4 mr-2' />
											{t('merchant.settings.linkCopiedButton')}
										</>
									) : (
										<>
											<Copy className='h-4 w-4 mr-2' />
											{t('merchant.settings.copyLinkButton')}
										</>
									)}
								</button>
							</div>
						</>
					) : (
						<p className='text-sm text-gray-500'>
							{t('merchant.settings.noRegistrationLink')}
						</p>
					)}
				</div>

				{/* Save Button */}
				<div className='bg-white rounded-xl shadow-sm border p-4 sm:p-6'>
					<div className='flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4'>
						<div>
							{isDirty && (
								<p className='text-sm text-amber-600 flex items-center'>
									<AlertCircle className='h-4 w-4 mr-1' />
									{t('merchant.settings.unsavedChanges')}
								</p>
							)}
						</div>
						<button
							type='submit'
							disabled={!isDirty || isSubmitting}
							className={`inline-flex items-center px-6 py-2.5 rounded-lg font-medium transition-colors ${
								isDirty && !isSubmitting
									? 'bg-blue-500 hover:bg-blue-600 text-white'
									: 'bg-gray-200 text-gray-500 cursor-not-allowed'
							}`}
						>
							<Save className='h-4 w-4 mr-2' />
							{isSubmitting ? t('common.saving') : t('merchant.settings.saveChanges')}
						</button>
					</div>
				</div>
			</form>
		</div>
	);
};

export default Settings;
